import classNames from 'classnames';
import { memo } from 'react';
import {
  Enum_Question_Type,
  Enum_Translation_Locale,
} from '@cyren/common-lib';
import Footer from '../../../../app/Footer';
import PaSurveyHeader from '../../../../app/PaSurveyHeader';
import LoadingAnim from '../../../../components/LoadingAnim';
import Question from '../../../../patient/tree/Question';
import PatientOrgContainer from '../../../../patient/hooks/PatientOrgContainer';
import type { UpdateAnswerHandler } from '../../../../patient/pa-prop-types';
import { useStaticLocales } from '../../../../admin/hooks/locales/use-static-locale';
import { usePatientLocale } from '../../../hooks/use-patient-locale';
import V2PaAdhocContainer from './V2PaAdhocContainer';

function V2PaAdhocHomePageContent() {
  const { localeCode } = usePatientLocale();
  const { slt, sltStr } = useStaticLocales({ localeCode });
  const {
    adhocState,
    updateAnswer,
    handleSave,
    hasQuestions,
    isValid,
    isLoading,
    isSaving,
    isError,
    errorMessage,
  } = V2PaAdhocContainer.useContainer();

  const level = 1;
  const dirty = false;
  const error = false;
  const errorMsgs = null;
  const treeKey = 'adhoc';
  const treeState = null;
  const updateValue = () => {};
  const tOptions = {};
  const onUiMetadataChange = undefined;
  const locale = 'english' as Enum_Translation_Locale;

  if (isLoading) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  return (
    <PatientOrgContainer.Provider>
      <div className='flex-col flex-1 w-full gap-5 px-5 pt-2'>
        <div className='flex-col flex-1 w-full gap-6'>
          <PaSurveyHeader />

          <div className='text-xl flex-col gap-3'>
            <div className='' />
            <div className='h-1' />

            <h4 className='font-bold sm'>{slt({ key: 'adhoc-title' })}</h4>
            <p className='md text-light'>
              {slt({
                key: hasQuestions
                  ? 'adhoc-subtitle'
                  : 'adhoc-subtitle-noquestions',
              })}
            </p>
          </div>

          <div className='flex-col gap-2'>
            {isError && (
              <div className='alert alert-error text-sm'>
                {errorMessage || 'Failed to load ad-hoc questions.'}
              </div>
            )}

            {adhocState.map((item) => {
              const question = {
                answerKeys: [],
                answerOptionsMap: {},
                createdAt: Date.now(),
                customAnswerLimit: null,
                desc: null,
                descInline: null,
                enabled: true,
                exampleKey: null,
                extNoteRequiredKeys: null,
                global: null,
                id: '0',
                images: null,
                optionConditions: [],
                questionKey: `q-adhoc-text-${item.uniqueId}`,
                required: true,
                showConditions: [],
                systemKey: null,
                type: 'text' as Enum_Question_Type,
                typeOptions: { max: '1', min: '1' },
                updatedAt: Date.now(),
              };

              const textOverrides = {
                questionText: item.translatedQuestionText || item.questionText,
                inlineDescription: item.translatedQuestionText
                  ? `   [Auto-translated from "${item.questionText}"]`
                  : undefined,
              };

              return (
                <Question
                  level={level}
                  dirty={dirty}
                  error={error}
                  errorMsgs={errorMsgs}
                  key={`adhoc-${item.uniqueId}`}
                  treeKey={treeKey}
                  question={question}
                  treeState={treeState}
                  updateAnswer={updateAnswer as UpdateAnswerHandler}
                  updateValue={updateValue}
                  tOptions={tOptions}
                  onUiMetadataChange={onUiMetadataChange}
                  locale={locale}
                  textOverrides={textOverrides}
                />
              );
            })}
          </div>

          <div className='flex-col gap-2'>
            {hasQuestions && (
              <button
                className={classNames(
                  'btn btn-lg btn-primary',
                  (!isValid || isSaving || isError) && 'btn-disabled',
                )}
                onClick={handleSave}
              >
                {isSaving && <span className='loading loading-spinner loading-xs' />}
                {sltStr({ key: 'btn-save', capit: true })}
              </button>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </PatientOrgContainer.Provider>
  );
}

function V2PaAdhocHomePage() {
  return (
    <V2PaAdhocContainer.Provider>
      <V2PaAdhocHomePageContent />
    </V2PaAdhocContainer.Provider>
  );
}

export default memo(V2PaAdhocHomePage);
